import React, { useState, useRef, useEffect } from "react";
import {
  Upload,
  Mic,
  ChevronDown,
  Loader2,
  Check,
  Copy,
  Download,
  Play,
  Square,
  Pause,
  RotateCcw,
  Trash2,
  Lock
} from "lucide-react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TokenBalanceCard from "../TokenBalanceCard";
import PlanStatusCard from "../PlanStatusCard";
import TranslationFeedback from "../TranslationFeedback";
import BetaModelBanner from "./BetaModelBanner";
import { getSubscription } from "../../../services/paymentService";
import { getApiKeys, getApiLogs } from "../../../services/developerService";
import { getUserById } from "../../../services/authService";
import { API_BASE_URL, LANGUAGES } from "../../../constant/constant";
import { transcribeSpeech } from "../../../services/speechService";
import { estimateAudioTokens, getAudioFileDuration, TOKEN_RATES } from "../../../utils/tokenCalculator";

const MAX_SPEECH_AUDIO_SECONDS = 30;



// Helper to format dates to time-ago strings
const formatTimeAgo = (date) => {
  const seconds = Math.floor((new Date() - date) / 1000);
  let interval = Math.floor(seconds / 31536000);

  if (interval >= 1) return interval === 1 ? '1 year ago' : `${interval} years ago`;
  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) return interval === 1 ? '1 month ago' : `${interval} months ago`;
  interval = Math.floor(seconds / 86400);
  if (interval >= 1) return interval === 1 ? '1 day ago' : `${interval} days ago`;
  interval = Math.floor(seconds / 3600);
  if (interval >= 1) return interval === 1 ? '1 hour ago' : `${interval} hours ago`;
  interval = Math.floor(seconds / 60);
  if (interval >= 1) return interval === 1 ? '1 minute ago' : `${interval} minutes ago`;
  return seconds < 10 ? 'just now' : `${Math.floor(seconds)} seconds ago`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const LanguageDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 font-medium bg-white hover:border-gray-300 transition-colors"
      >
        <span>{value}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg py-1 max-h-48 overflow-y-auto">
          {LANGUAGES.map((lang) => (
            <button
              key={lang}
              onClick={() => { onChange(lang); setOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${value === lang
                ? "bg-[#F0FAF6] text-[#2D7A5F] font-semibold"
                : "text-gray-700 hover:bg-gray-50"
                }`}
            >
              {lang}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const UploadZone = ({ onFile, file, onClear }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  };

  if (file) {
    return (
      <div className="rounded-2xl bg-[#EEF5F3] border border-[#C8E0D8] p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white border border-[#C8E0D8] flex items-center justify-center flex-shrink-0">
            <Upload className="w-4 h-4 text-[#2D7A5F]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate max-w-[200px]">{file.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {(file.size / (1024 * 1024)).toFixed(2)} MB · {file.type || "audio"}
            </p>
          </div>
        </div>
        <button
          onClick={onClear}
          className="w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
        >
          <span className="text-sm leading-none">×</span>
        </button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={`rounded-2xl p-10 text-center cursor-pointer transition-all duration-200 ${dragging
        ? "border-2 border-dashed border-[#2D7A5F] bg-[#F0FAF6]"
        : "border-2 border-dashed border-gray-200 bg-[#EEF5F3] hover:border-[#B2D8CC] hover:bg-[#E6F2EF]"
        }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={(e) => {
          const selected = e.target.files?.[0];
          e.target.value = "";
          if (selected) onFile(selected);
        }}
      />
      <div className="flex flex-col items-center gap-2">
        <div className="w-10 h-10 rounded-full bg-[#2D7A5F]/10 flex items-center justify-center mb-1">
          <Upload className="w-5 h-5 text-[#2D7A5F]" />
        </div>
        <p className="text-sm font-semibold text-gray-800">Drop audio file or click to upload</p>
        <p className="text-xs text-gray-400">MP3 or WAV · up to 30 seconds · 25 MB</p>
      </div>
    </div>
  );
};

const HistoryItem = ({ item }) => (
  <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
    <div className="min-w-0 pr-4">
      <p className="text-sm font-semibold text-gray-900 truncate">{item.title}</p>
      <div className="flex items-center gap-3 mt-1">
        <span className="text-xs text-gray-500">{item.lang}</span>
        <span className="text-xs text-gray-500">Tokens used: <span className="font-semibold text-gray-700">{item.tokens}</span></span>
      </div>
    </div>
    <span className="text-xs text-gray-400 flex-shrink-0">{item.time}</span>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const SpeechToText = () => {
  const { token } = useSelector((state) => state.auth);

  // Subscription & User Details State
  const [checkingData, setCheckingData] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [remainingDays, setRemainingDays] = useState(null);
  const [apiKey, setApiKey] = useState(null);

  // STT Config & State
  const [activeTab, setActiveTab] = useState("upload"); // "upload" | "record"
  const [language, setLanguage] = useState("English");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [copied, setCopied] = useState(false);
  const [copiedApi, setCopiedApi] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);

  const [stats, setStats] = useState({
    words: 0,
    tokensConsumed: 0,
    duration: "0 : 00",
    processingTime: "0.00s",
    durationSecs: 0,
  });

  const [history, setHistory] = useState([]);

  // Web Audio Recording States
  const [recordingState, setRecordingState] = useState("idle");
  const [recordDuration, setRecordDuration] = useState(0);
  const timerRef = useRef(null);
  const recordSecondsRef = useRef(0);

  // MediaRecorder references
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [audioUrl, setAudioUrl] = useState(null);

  // Playback preview reference
  const previewAudioRef = useRef(null);
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);

  // Retrieve details and logs
  const fetchDashboardData = async () => {
    if (!token) return;
    try {
      // 1. User details
      const userRes = await getUserById(token);
      if (userRes) {
        setUserDetails(userRes);

        if (userRes.tokenExpiration) {
          const now = new Date();
          const exp = new Date(userRes.tokenExpiration);
          const diffTime = exp.getTime() - now.getTime();
          const diffDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
          setRemainingDays(diffDays);
        }
      }

      // 2. Subscription status
      const subResponse = await getSubscription(token);
      if (subResponse.success && subResponse.subscription) {
        setSubscriptionDetails(subResponse.subscription);
      }

      // 3. API Keys
      const keysResponse = await getApiKeys(token);
      const activeKey = keysResponse.apiKeys?.find(key => key.isActive);
      if (activeKey) {
        setApiKey(activeKey.key);
      }

      // 4. API Logs for history
      const logsResponse = await getApiLogs({ limit: 20 }, token);
      if (logsResponse?.logs) {
        const sttLogs = logsResponse.logs.filter(
          log => (log.service && log.service.toUpperCase() === 'STT') || (log.endpoint && log.endpoint.includes('/stt/'))
        );
        const mappedHistory = sttLogs.map((log) => {
          const actionParts = (log.action || '').split(' • ');

          const time = log.when || (log.createdAt ? formatTimeAgo(new Date(log.createdAt)) : '');

          let lang = log.language || 'English';
          if (actionParts[0]) {
            const langMatch = actionParts[0].match(/^Speech\s+to\s+(.*)/i);
            if (langMatch && langMatch[1]) {
              lang = langMatch[1].charAt(0).toUpperCase() + langMatch[1].slice(1).toLowerCase();
            }
          } else if (log.endpoint) {
            const match = log.endpoint.match(/\/stt\/v1\/([^/]+)/);
            if (match && match[1]) {
              lang = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
            }
          }

          let filename = 'Audio file';
          const fileSource = actionParts[1] || log.input || '';
          const nameMatch = fileSource.match(/Audio File:\s*([^\]]+)/i);
          if (nameMatch && nameMatch[1]) {
            filename = nameMatch[1].trim();
          } else if (fileSource) {
            filename = fileSource.replace(/^\[|\]$/g, '').trim();
          }

          const title = `Transcribed speech (${filename})`;
          const tokens = log.token ?? log.tokensUsed ?? 0;

          return {
            title,
            lang,
            tokens,
            time
          };
        });
        setHistory(mappedHistory);
      }

    } catch (error) {
      console.error("Error loading dashboard data for STT:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setCheckingData(true);
      await fetchDashboardData();
      setCheckingData(false);
    };
    init();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const checkTokenStatus = () => {
    if (!userDetails) {
      return { expired: false, exhausted: true };
    }
    const tokensAvailable = userDetails.tokens !== undefined && userDetails.tokens !== null && userDetails.tokens > 0;
    const exhausted = !tokensAvailable;
    let expired = false;
    if (userDetails.tokenExpiration) {
      const expirationDate = new Date(userDetails.tokenExpiration);
      const currentDate = new Date();
      expired = currentDate > expirationDate;
    }
    return { expired, exhausted };
  };

  // Perform STT API Call
  const handleTranscribe = async () => {
    if (!uploadedFile || loading) return;

    const { expired, exhausted } = checkTokenStatus();
    if (expired) {
      toast.error("Your tokens have expired. Please purchase a plan or renew your subscription.");
      return;
    }
    if (exhausted) {
      toast.error("Insufficient tokens. Please purchase a top-up plan.");
      return;
    }

    let durSecs = activeTab === "upload" ? audioDuration : recordDuration;
    if ((!durSecs || !Number.isFinite(durSecs)) && uploadedFile) {
      durSecs = await getAudioFileDuration(uploadedFile);
    }
    const safeDur = Number.isFinite(durSecs) && durSecs > 0 ? durSecs : 0;
    if (safeDur <= 0) {
      toast.error("Couldn't read this audio length. Use an MP3 or WAV that is 30 seconds or shorter.");
      return;
    }
    if (safeDur > MAX_SPEECH_AUDIO_SECONDS) {
      toast.error(`Audio must be ${MAX_SPEECH_AUDIO_SECONDS} seconds or shorter.`);
      return;
    }

    setLoading(true);
    setTranscript("");
    const startTime = Date.now();

    try {
      const result = await transcribeSpeech(uploadedFile, language, token, safeDur);

      if (result.status === "success" || result.transcription) {
        const transText = result.transcription || "";
        setTranscript(transText);
        setShowFeedback(true);
        toast.success("Audio transcribed successfully!");
        const procSecs = ((Date.now() - startTime) / 1000).toFixed(2);
        const wordCount = transText.trim().split(/\s+/).filter(Boolean).length;
        const tokens = Number.isFinite(result.tokensUsed) ? result.tokensUsed : estimateAudioTokens(safeDur, TOKEN_RATES.STT_TOKENS_PER_SECOND);

        const mins = Math.floor(safeDur / 60);
        const secs = Math.round(safeDur % 60);
        const formattedDur = `${mins} : ${String(secs).padStart(2, "0")}`;

        setStats({
          words: wordCount,
          tokensConsumed: tokens,
          duration: formattedDur,
          processingTime: `${procSecs}s`,
          durationSecs: safeDur,
        });

        // Refresh token balances & history logs
        await fetchDashboardData();
      } else {
        throw new Error(result.message || "Failed to transcribe speech.");
      }
    } catch (error) {
      console.error("Transcription error:", error);
      toast.error(error.message || "Failed to transcribe audio.");
    } finally {
      setLoading(false);
    }
  };

  // Web Audio Recording functionality
  const stopRecordingAtLimit = () => {
    stopRecording();
    toast.error(`Audio is limited to ${MAX_SPEECH_AUDIO_SECONDS} seconds.`);
  };

  const beginDurationTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const next = recordSecondsRef.current + 1;
      if (next >= MAX_SPEECH_AUDIO_SECONDS) {
        recordSecondsRef.current = MAX_SPEECH_AUDIO_SECONDS;
        setRecordDuration(MAX_SPEECH_AUDIO_SECONDS);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        stopRecordingAtLimit();
        return;
      }
      recordSecondsRef.current = next;
      setRecordDuration(next);
    }, 1000);
  };

  const startRecording = async () => {
    audioChunksRef.current = [];
    recordSecondsRef.current = 0;
    setRecordDuration(0);
    setTranscript("");
    setUploadedFile(null);
    setAudioUrl(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/mp3" });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        const recordFile = new File([audioBlob], `recording-${Date.now()}.mp3`, {
          type: "audio/mp3",
          lastModified: Date.now(),
        });
        setUploadedFile(recordFile);
      };

      mediaRecorder.start();
      setRecordingState("recording");
      beginDurationTimer();
    } catch (err) {
      console.error("Microphone access denied or unsupported", err);
      toast.error("Could not access microphone. Please confirm browser permissions.");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.pause();
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecordingState("paused");
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
      mediaRecorderRef.current.resume();
    }
    setRecordingState("recording");
    beginDurationTimer();
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecordingState("finished");
  };

  const deleteRecording = () => {
    setRecordingState("idle");
    setUploadedFile(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    recordSecondsRef.current = 0;
    setRecordDuration(0);
    setIsPlayingPreview(false);
    setPreviewProgress(0);
  };

  const handleClear = () => {
    deleteRecording();
    setTranscript("");
    setShowFeedback(false);
    setAudioDuration(0);
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current.currentTime = 0;
    }
  };

  // Preview Playback Controls
  const handleTogglePreviewPlay = () => {
    if (!previewAudioRef.current) return;
    if (isPlayingPreview) {
      previewAudioRef.current.pause();
      setIsPlayingPreview(false);
    } else {
      previewAudioRef.current.play().then(() => {
        setIsPlayingPreview(true);
      }).catch(err => {
        console.error("Preview playback failed:", err);
      });
    }
  };

  const handlePreviewAudioTimeUpdate = () => {
    if (!previewAudioRef.current) return;
    const dur = previewAudioRef.current.duration;
    const progress = (Number.isFinite(dur) && dur > 0) ? (previewAudioRef.current.currentTime / dur) : 0;
    setPreviewProgress(isNaN(progress) || !Number.isFinite(progress) ? 0 : progress);
  };

  const handlePreviewAudioEnded = () => {
    setIsPlayingPreview(false);
    setPreviewProgress(0);
  };

  const handleFile = async (file) => {
    if (!file) {
      setUploadedFile(null);
      setAudioDuration(0);
      return;
    }

    const dur = await getAudioFileDuration(file);
    if (!Number.isFinite(dur) || dur <= 0) {
      setUploadedFile(null);
      setAudioDuration(0);
      toast.error("Couldn't read this audio length. Use an MP3 or WAV that is 30 seconds or shorter.");
      return;
    }
    if (dur > MAX_SPEECH_AUDIO_SECONDS) {
      setUploadedFile(null);
      setAudioDuration(0);
      toast.error(`Audio must be ${MAX_SPEECH_AUDIO_SECONDS} seconds or shorter.`);
      return;
    }

    setUploadedFile(file);
    setTranscript("");
    setAudioDuration(dur);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(transcript);
    setCopied(true);
    toast.success("Transcript copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportText = () => {
    if (!transcript) return;
    const element = document.createElement("a");
    const file = new Blob([transcript], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `transcription-${language.toLowerCase()}-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success("Transcript exported successfully!");
  };

  const handleExportJSON = () => {
    if (!transcript) return;
    const jsonObject = {
      language,
      words: stats.words,
      tokensConsumed: stats.tokensConsumed,
      duration: stats.duration,
      processingTime: stats.processingTime,
      transcription: transcript,
    };
    const blob = new Blob([JSON.stringify(jsonObject, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const element = document.createElement("a");
    element.href = url;
    element.download = `transcription-${language.toLowerCase()}-${Date.now()}.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(url);
    toast.success("JSON exported successfully!");
  };

  const handleExportSRT = () => {
    if (!transcript) return;

    const formatSrtTime = (seconds) => {
      const hrs = Math.floor(seconds / 3600).toString().padStart(2, "0");
      const mins = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
      const secs = Math.floor(seconds % 60).toString().padStart(2, "0");
      const ms = Math.floor((seconds % 1) * 1000).toString().padStart(3, "0");
      return `${hrs}:${mins}:${secs},${ms}`;
    };

    const sentences = transcript.match(/[^.!?]+[.!?]+/g) || [transcript];
    const totalDuration = stats.durationSecs || Math.max(sentences.length * 3, 5);
    const sentenceDuration = totalDuration / sentences.length;

    let srtContent = "";
    sentences.forEach((sentence, index) => {
      const startTime = index * sentenceDuration;
      const endTime = (index + 1) * sentenceDuration;
      srtContent += `${index + 1}\n${formatSrtTime(startTime)} --> ${formatSrtTime(endTime)}\n${sentence.trim()}\n\n`;
    });

    const blob = new Blob([srtContent.trim()], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const element = document.createElement("a");
    element.href = url;
    element.download = `transcription-${language.toLowerCase()}-${Date.now()}.srt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    URL.revokeObjectURL(url);
    toast.success("SRT subtitle file exported!");
  };

  const handleCopyApi = () => {
    const curl = `curl -X 'POST' \\
  '${API_BASE_URL}/stt/v1/${language.toLowerCase()}/with-api-key' \\
  -H 'accept: application/json' \\
  -H 'Authorization: Bearer ${apiKey || 'YOUR_API_KEY_HERE'}' \\
  -H 'Content-Type: multipart/form-data' \\
  -F 'audio_file=@${uploadedFile ? uploadedFile.name : 'recording.mp3'};type=audio/mpeg'`;
    navigator.clipboard.writeText(curl);
    setCopiedApi(true);
    toast.success("Curl command request copied!");
    setTimeout(() => setCopiedApi(false), 2000);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setTranscript("");
    setUploadedFile(null);
    setRecordingState("idle");
    recordSecondsRef.current = 0;
    setRecordDuration(0);
    setIsPlayingPreview(false);
    setPreviewProgress(0);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    if (timerRef.current) clearInterval(timerRef.current);
  };

  if (checkingData) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const { expired: tokensExpired, exhausted: tokensExhausted } = checkTokenStatus();
  const lockUi = tokensExpired || tokensExhausted;

  if (lockUi) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[600px]">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 max-w-xl w-full text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Premium feature locked
          </h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            This Speech-to-Text tool is premium and requires an active subscription or valid token balance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 mx-auto space-y-5">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── Audio element for preview playback ── */}
      {audioUrl && (
        <audio
          ref={previewAudioRef}
          src={audioUrl}
          onTimeUpdate={handlePreviewAudioTimeUpdate}
          onEnded={handlePreviewAudioEnded}
          className="hidden"
        />
      )}

      {/* ── Beta Models Banner ── */}
      <BetaModelBanner />

      {/* ── Row 1: Token Balance + Plan Status ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TokenBalanceCard
          userDetails={userDetails}
          remainingDays={remainingDays}
          subscriptionDetails={subscriptionDetails}
        />
        <PlanStatusCard
          subscriptionDetails={subscriptionDetails}
        />
      </div>

      {/* ── Row 2: Input Panel (50%) + Language/Transcribe Panel (50%) ── */}
      <div className="flex gap-4 flex-col lg:flex-row items-start">

        {/* Left: Upload / Record tabs + zone */}
        <div className="w-full lg:w-1/2 bg-white border border-gray-200 rounded-2xl p-5">
          {/* Tab Switcher */}
          <div className="flex gap-1 mb-5">
            <button
              onClick={() => switchTab("upload")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 border ${activeTab === "upload"
                ? "bg-white border-gray-200 text-gray-900 shadow-sm"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
            <button
              onClick={() => switchTab("record")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 border ${activeTab === "record"
                ? "bg-white border-gray-200 text-gray-900 shadow-sm"
                : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              <Mic className="w-4 h-4" />
              Record
            </button>
          </div>

          {/* Upload Zone */}
          {activeTab === "upload" && (
            <UploadZone
              onFile={handleFile}
              file={uploadedFile}
              onClear={() => { setUploadedFile(null); setTranscript(""); setAudioDuration(0); }}
            />
          )}

          {/* Advanced Record Zone */}
          {activeTab === "record" && (
            <div className="rounded-2xl bg-[#EEF5F3] border border-[#C8E0D8] p-6 flex flex-col gap-6">

              {/* State 1: IDLE */}
              {recordingState === "idle" && (
                <div className="flex flex-col gap-6">
                  <div className="flex justify-between items-center text-xs font-semibold text-gray-600 tracking-wider">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                      <span>READY TO RECORD</span>
                    </div>
                    <span>{formatDuration(0)} / {formatDuration(MAX_SPEECH_AUDIO_SECONDS)}</span>
                  </div>

                  <div className="h-16 flex items-center justify-center bg-gray-200/50 rounded-xl border border-dashed border-gray-300">
                    <span className="text-gray-400 font-mono">----------------------------------------</span>
                  </div>

                  <div className="flex justify-center">
                    <button
                      onClick={startRecording}
                      className="flex items-center gap-2 bg-[#D13B3B] hover:bg-[#B22E2E] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors shadow-md"
                    >
                      <Mic className="w-4 h-4" />
                      Start recording
                    </button>
                  </div>
                </div>
              )}

              {/* State 2: RECORDING */}
              {recordingState === "recording" && (
                <div className="flex flex-col gap-6">
                  <div className="flex justify-between items-center text-xs font-semibold text-[#D13B3B] tracking-wider">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#D13B3B] animate-pulse" />
                      <span>RECORDING</span>
                    </div>
                    <span className="font-mono text-gray-700">{formatDuration(recordDuration)} / {formatDuration(MAX_SPEECH_AUDIO_SECONDS)}</span>
                  </div>

                  <div className="h-16 flex items-center justify-center bg-gray-200/50 rounded-xl border border-dashed border-gray-300">
                    <span className="text-[#D13B3B] font-mono animate-pulse">----------------------------------------</span>
                  </div>

                  <div className="flex justify-center gap-3">
                    <button
                      onClick={pauseRecording}
                      className="flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800 text-sm font-semibold px-6 py-2.5 rounded-full transition-colors shadow-sm"
                    >
                      <Pause className="w-4 h-4 text-gray-700 fill-gray-700" />
                      Pause
                    </button>
                    <button
                      onClick={stopRecording}
                      className="flex items-center gap-2 bg-[#0C101A] hover:bg-[#1C202A] text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors shadow-md"
                    >
                      <Square className="w-4 h-4 text-white fill-white" />
                      Stop
                    </button>
                  </div>
                </div>
              )}

              {/* State 3: PAUSED */}
              {recordingState === "paused" && (
                <div className="flex flex-col gap-6">
                  <div className="flex justify-between items-center text-xs font-semibold text-[#ef8702] tracking-wider">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#ef8702]" />
                      <span>PAUSED</span>
                    </div>
                    <span className="font-mono text-gray-700">{formatDuration(recordDuration)} / {formatDuration(MAX_SPEECH_AUDIO_SECONDS)}</span>
                  </div>

                  <div className="h-16 flex items-center justify-center bg-gray-200/50 rounded-xl border border-dashed border-gray-300">
                    <span className="text-gray-400 font-mono">----------------------------------------</span>
                  </div>

                  <div className="flex justify-center gap-3">
                    <button
                      onClick={resumeRecording}
                      className="flex items-center gap-2 bg-[#1e6c56] hover:bg-[#175443] text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors shadow-md"
                    >
                      <Play className="w-4 h-4 text-white fill-white" />
                      Resume
                    </button>
                    <button
                      onClick={stopRecording}
                      className="flex items-center gap-2 bg-[#0C101A] hover:bg-[#1C202A] text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-colors shadow-md"
                    >
                      <Square className="w-4 h-4 text-white fill-white" />
                      Stop
                    </button>
                  </div>
                </div>
              )}

              {/* State 4: FINISHED */}
              {recordingState === "finished" && uploadedFile && (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between bg-white border border-[#C8E0D8] p-4 rounded-xl flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#EEF5F3] flex items-center justify-center text-[#2D7A5F]">
                        <Mic className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 truncate max-w-[150px]">{uploadedFile.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">Recording · {(uploadedFile.size / 1024).toFixed(1)} KB · {formatDuration(recordDuration)}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={startRecording}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Re-record
                      </button>
                      <button
                        onClick={deleteRecording}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-100 hover:bg-red-50 text-xs font-semibold text-red-500"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Built-in Recording Preview Player */}
                  <div className="flex items-center gap-3 px-1">
                    <button
                      onClick={handleTogglePreviewPlay}
                      className="w-9 h-9 rounded-full bg-[#1e6c56] text-white flex items-center justify-center hover:bg-[#175443] transition-all flex-shrink-0"
                    >
                      {isPlayingPreview ? (
                        <Pause className="w-4 h-4 fill-white text-white" />
                      ) : (
                        <Play className="w-4 h-4 fill-white text-white ml-0.5" />
                      )}
                    </button>
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full relative overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full bg-[#1e6c56]"
                        style={{ width: `${previewProgress * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 font-mono">{formatDuration(recordDuration)}</span>
                  </div>
                </div>
              )}

            </div>
          )}
        </div>

        {/* Right: Language + Transcribe */}
        <div className="w-full lg:w-1/2 bg-white border border-gray-200 rounded-2xl p-5 space-y-5">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
              Language
            </p>
            <LanguageDropdown value={language} onChange={setLanguage} />
          </div>

          <div className="flex justify-end gap-3 flex-wrap">
            {uploadedFile && (
              <button
                onClick={handleCopyApi}
                className="flex items-center gap-2 border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
              >
                {copiedApi ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                Copy API request
              </button>
            )}
            {uploadedFile && estimateAudioTokens(activeTab === "upload" ? audioDuration : recordDuration, TOKEN_RATES.STT_TOKENS_PER_SECOND) > 0 && (
              <span className="text-xs text-gray-500 self-center">
                Estimated: {estimateAudioTokens(activeTab === "upload" ? audioDuration : recordDuration, TOKEN_RATES.STT_TOKENS_PER_SECOND)} {estimateAudioTokens(activeTab === "upload" ? audioDuration : recordDuration, TOKEN_RATES.STT_TOKENS_PER_SECOND) === 1 ? 'Token' : 'Tokens'}
              </span>
            )}
            <button
              type="button"
              onClick={handleClear}
              disabled={!uploadedFile && !transcript}
              className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors bg-white"
              title="Clear audio file and transcription"
            >
              <RotateCcw className="w-4 h-4 text-gray-500" />
              Clear
            </button>
            <button
              onClick={handleTranscribe}
              disabled={loading || !uploadedFile}
              className="flex items-center gap-2 bg-[#1e6c56] hover:bg-[#175443] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? "Transcribing…" : "Transcribe Speech"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Stats & Actions Card ── */}
      {transcript && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          {/* Top 4 Stats Columns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6 border-b border-gray-100 pb-6">
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">WORDS</p>
              <p className="text-2xl font-black text-gray-900">{stats.words}</p>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">TOKENS CONSUMED</p>
              <p className="text-2xl font-black text-gray-900">{stats.tokensConsumed}</p>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">DURATION</p>
              <p className="text-2xl font-black text-gray-900">{stats.duration}</p>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">PROCESSING</p>
              <p className="text-2xl font-black text-gray-900">{stats.processingTime}</p>
            </div>
          </div>

          {/* Action Buttons Row */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors shadow-sm"
            >
              <Download className="w-4 h-4 text-gray-700" />
              {copied ? "Copied!" : "Copy Text"}
            </button>
            <button
              onClick={handleExportText}
              className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors shadow-sm"
            >
              <Download className="w-4 h-4 text-gray-700" />
              Download TXT
            </button>
            <button
              onClick={handleExportJSON}
              className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors shadow-sm"
            >
              <Download className="w-4 h-4 text-gray-700" />
              Download JSON
            </button>
            <button
              onClick={handleExportSRT}
              className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors shadow-sm"
            >
              <Download className="w-4 h-4 text-gray-700" />
              Download SRT
            </button>
          </div>
        </div>
      )}

      {/* ── Row 3: Transcript Area ── */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5 min-h-[200px]">
        {loading && !transcript ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3">
            <Loader2 className="w-6 h-6 text-[#2D7A5F] animate-spin" />
            <p className="text-sm text-gray-400">Processing audio…</p>
          </div>
        ) : transcript ? (
          <>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <span className="text-sm font-semibold text-gray-700">Transcript</span>
            </div>
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{transcript}</p>
          </>
        ) : (
          <p className="text-sm text-gray-300">Transcript will appear here...</p>
        )}
      </div>

      {/* ── Translation Feedback Card ── */}
      {transcript && !loading && showFeedback && (
        <TranslationFeedback
          input={uploadedFile ? uploadedFile.name : "Speech recording"}
          output={transcript}
          fromLanguage={language}
          toLanguage="Text"
          apiEndpoint="/stt/v1/transcribe"
          onHide={() => setShowFeedback(false)}
        />
      )}

      {/* ── Row 4: Transcription History ── */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Transcription History</h2>
        {history.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">No speech-to-text transcribing history yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {history.map((item, i) => (
              <HistoryItem key={i} item={item} />
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default SpeechToText;
